/**
 * FirmClientView — /w/:slug/app/audit-firms/:engagementId[?preview=auditor]
 *
 * V98-6 / ADR-0047 §2: the client's side of a firm engagement — the
 * client-visible tier only (rendered by FirmClientTier on the server from an
 * allow-list). Never the firm's working papers, never an opinion.
 *
 * Track 6 review:
 *   T6-B5  "Record source" inline for a population without provenance.
 *   T6-B11 a stale representation letter offers "Re-date and resend".
 *   T6-H5  assertion / letters open an editable draft from the template
 *          ("Template in review" tag), then "Submit to {firm}" with a confirm;
 *          a returned document shows the firm's comment above the editor.
 *   T6-H6  "Preview as auditor" lists exactly what it shows, plus "Also
 *          shared with the firm:" — never "everything".
 *   T6-H7  attach several items or upload a file inline; reply in the
 *          thread; owners/admins assign an owner.
 *   T6-H9  the owner can revoke client access (custody explained, reason required).
 *   Medium "Owed to the auditor (N)" at the top; read-only sections as
 *          definition lists; "Respond to v2" is status text that focuses the
 *          response field; non-admins see who responds.
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea } from '@aigovops/design-system';
import { ActionDialog, useAnnounce } from '../components/today/shared';
import { useOptionalWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import { listWorkspaceMembers, req } from '../lib/api';
import { clientFirmApi, type ClientException, type ClientPopulation, type ClientTier, type PbcRequest, type ReportDocument } from '../lib/firm-api';
import { fmtDate, fmtRange, plural } from '../lib/format';
import { Chip, StateBadge, TemplateNotice, errText } from './firm/firm-kit';
import './firm.css';
import './today.css';

const DOC_KINDS = ['management_assertion', 'representation_letter', 'bridge_letter'] as const;
const DOC_LABEL: Record<string, string> = { management_assertion: 'Management assertion', representation_letter: 'Representation letter', bridge_letter: 'Bridge letter' };
const CLIENT_STATE: Record<string, string> = { requested: 'Requested', submitted: 'Sent · with the auditor', returned: 'Returned · needs a new version', accepted: 'Accepted', withdrawn: 'Withdrawn' };

interface Ctx {
  slug: string; eng: string; t: ClientTier; admin: boolean; editor: boolean; preview: boolean; open: boolean; userId: string | undefined;
  act: (fn: () => Promise<unknown>, ok: string) => Promise<boolean>; busy: boolean;
}

export function FirmClientView() {
  const { slug = '', engagementId = '' } = useParams();
  const [params] = useSearchParams();
  const preview = params.get('preview') === 'auditor';
  const ws = useOptionalWorkspace();
  const { user } = useAuth();
  const role = ws?.role;
  const admin = !preview && (role === 'owner' || role === 'admin');
  const editor = !preview && (role === 'owner' || role === 'admin' || role === 'editor');
  const qc = useQueryClient();
  const key = ['firm-client-tier', slug, engagementId];
  const tier = useQuery({ queryKey: key, queryFn: () => clientFirmApi.tier(slug, engagementId) });
  const [announcer, announce] = useAnnounce();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    if (busy) return false;
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
      announce(ok);
      return true;
    } catch (e) {
      setErr(errText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (tier.isLoading) return <Spinner label="Loading the engagement" />;
  if (tier.error || !tier.data) return <EmptyState title="Engagement not found" action={<Link to={`/w/${slug}/app/settings/audit-firms`}>Audit firms</Link>} />;
  const t = tier.data;
  const open = t.engagement.client_access === 'active';
  const c: Ctx = { slug, eng: engagementId, t, admin, editor, preview, open, userId: user?.id, act, busy };

  const owedRequests = t.requests.filter((r) => r.status === 'requested' || r.status === 'returned');
  const owedPops = t.populations.filter((p) => !p.provenance);
  const owedResponses = t.exceptions.filter((x) => !x.response || x.needs_response_update);
  const owedDocs = t.documents.filter((d) => d.status === 'returned' || d.stale);
  const owed = owedRequests.length + owedPops.length + owedResponses.length + owedDocs.length;

  return (
    <div className="gp-firm-stack">
      <PageHeader title={`Audit · ${t.engagement.firm_name}`} subtitle={`Engagement ${fmtRange(t.engagement.engagement_start, t.engagement.engagement_end)}${t.engagement.report_date ? ` · report ${fmtDate(t.engagement.report_date)}` : ''}`} />
      {preview ? <PreviewScope firm={t.engagement.firm_name} slug={slug} eng={engagementId} /> : null}
      <p className="gp-firm-meta">
        <span>Report status: <Chip tone={t.report_status.status === 'issued' ? 'success' : 'neutral'}>{t.report_status.label}</Chip></span>
        {!open ? <span><Chip tone="danger">Access revoked</Chip></span> : null}
        {!preview ? <span><Link to="?preview=auditor">Preview as auditor</Link></span> : null}
        {!preview && role === 'owner' && open ? <span><Button compact variant="ghost" onClick={() => setRevoking(true)}>Revoke access…</Button></span> : null}
      </p>
      {!preview && open && owed > 0 ? (
        <p className="gp-firm-owed">
          <strong>Owed to the auditor ({owed})</strong>
          {owedRequests.length ? <a href="#requests">{plural(owedRequests.length, 'request')}</a> : null}
          {owedPops.length ? <a href="#populations">{plural(owedPops.length, 'population source')}</a> : null}
          {owedResponses.length ? <a href="#exceptions">{plural(owedResponses.length, 'response')}</a> : null}
          {owedDocs.length ? <a href="#documents">{plural(owedDocs.length, 'document')}</a> : null}
        </p>
      ) : null}
      {err ? <Alert variant="error">{err}</Alert> : null}

      <Card title={`Requests (${t.requests.length})`}>
        <div id="requests" className="gp-firm-stack">
          {t.requests.length === 0 ? <p className="gp-firm-muted">No requests from the firm yet.</p> : (
            <ul className="gp-firm-list">{t.requests.map((r) => <RequestRow key={r.request_id} r={r} c={c} />)}</ul>
          )}
        </div>
      </Card>

      {t.populations.length ? (
        <Card title="Populations">
          <ul className="gp-firm-list" id="populations">{t.populations.map((p) => <PopulationRow key={p.population_id} p={p} c={c} />)}</ul>
        </Card>
      ) : null}

      <Card title="Exceptions">
        <div id="exceptions">
          {t.exceptions.length === 0 ? <p className="gp-firm-muted">None issued.</p> : (
            <ul className="gp-firm-list">{t.exceptions.map((x) => <ExceptionRow key={x.exception_id} x={x} c={c} />)}</ul>
          )}
        </div>
      </Card>

      <Card title="Report documents">
        <div id="documents" className="gp-firm-stack">
          <TemplateNotice detail={t.documents.find((d) => d.template_review_status)?.template_review_status ?? (admin && open ? 'pending' : null)} />
          <ul className="gp-firm-list">{DOC_KINDS.map((k) => <DocumentRow key={k} kind={k} doc={t.documents.filter((d) => d.kind === k).sort((a, b) => b.version - a.version)[0] ?? null} c={c} />)}</ul>
        </div>
      </Card>

      {revoking ? <RevokeDialog c={c} onClose={() => setRevoking(false)} /> : null}
      {announcer}
    </div>
  );
}

/** T6-H6: exactly what the preview shows, and what else the firm can read. */
function PreviewScope({ firm, slug, eng }: { firm: string; slug: string; eng: string }) {
  return (
    <Alert variant="info" title="Preview as auditor">
      <p className="gp-firm-muted">What {firm} sees of your side on this page: requests with what you sent and the thread · populations, their recorded source and the items selected for testing · issued exceptions and your responses · report documents · the report status.</p>
      <p className="gp-firm-muted">Also shared with the firm: the evidence items in each version you send · population attributes · system description versions · segregation-of-duties and delegation logs.</p>
      <p className="gp-firm-muted">Their working papers, review notes and opinion drafts never appear here. <Link to={`/w/${slug}/app/audit-firms/${eng}`}>Back to your view</Link></p>
    </Alert>
  );
}

function RequestRow({ r, c }: { r: PbcRequest; c: Ctx }) {
  const latest = r.versions[r.versions.length - 1];
  const canSubmit = c.open && !c.preview && (r.status === 'requested' || r.status === 'returned') && (c.admin || (c.editor && r.owner_user_id === c.userId));
  const canReply = c.open && !c.preview && r.status !== 'withdrawn' && (c.editor || r.owner_user_id === c.userId);
  const [reply, setReply] = useState('');
  const [tried, setTried] = useState(false);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!reply.trim()) return;
    if (await c.act(() => clientFirmApi.pbcMessage(c.slug, c.eng, r.request_id, reply.trim()), `Message sent on ${r.ref}`)) { setReply(''); setTried(false); }
  };
  return (
    <li className="gp-firm-row" id={`req-${r.request_id}`}>
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{r.ref} · {r.title}</h3>
        <StateBadge state={r.status} label={CLIENT_STATE[r.status]} tone={r.status === 'requested' || r.status === 'returned' ? 'accent' : r.status === 'accepted' ? 'success' : 'neutral'} />
      </div>
      <dl className="gp-firm-dl">
        {r.control_ref ? <><dt>Control</dt><dd>{r.control_ref}</dd></> : null}
        {r.due_at ? <><dt>Due</dt><dd>{fmtDate(r.due_at)}</dd></> : null}
        {r.detail ? <><dt>Detail</dt><dd>{r.detail}</dd></> : null}
        <dt>Owner</dt>
        <dd>{c.admin && c.open && r.status !== 'accepted' && r.status !== 'withdrawn' ? <OwnerPicker r={r} c={c} /> : (r.owner_name ?? 'No owner yet')}</dd>
        {latest ? <><dt>Sent</dt><dd>v{latest.version} · {plural(latest.items.length, 'item')} · {fmtDate(latest.submitted_at)}{latest.backfill_flag ? ' · includes evidence created after the request' : ''}</dd></> : null}
      </dl>
      {r.return_reason && r.status === 'returned' ? <p className="gp-firm-muted">Returned: {r.return_reason}</p> : null}
      {canSubmit ? <Attach r={r} c={c} hasVersion={!!latest} /> : null}
      {r.thread.length ? (
        <ul className="gp-firm-thread" aria-label={`Messages on ${r.ref}`}>
          {r.thread.map((m) => <li key={m.message_id}><strong>{m.author}</strong> · {fmtDate(m.posted_at)}: {m.body}</li>)}
        </ul>
      ) : null}
      {canReply ? (
        <form className="gp-firm-form gp-firm-form--row" onSubmit={send} noValidate>
          <Field id={`creply-${r.request_id}`} label={`Reply on ${r.ref}`} error={tried && !reply.trim() ? 'Write a message first.' : null}>
            <Input value={reply} onChange={(e) => setReply(e.target.value)} />
          </Field>
          <div className="gp-firm-actions"><Button type="submit" compact aria-disabled={c.busy || undefined}>Send</Button></div>
        </form>
      ) : null}
    </li>
  );
}

function OwnerPicker({ r, c }: { r: PbcRequest; c: Ctx }) {
  const members = useQuery({ queryKey: ['ws-members', c.slug], queryFn: () => listWorkspaceMembers(c.slug) });
  return (
    <Select aria-label={`Owner of ${r.ref}`} value={r.owner_user_id ?? ''}
      onChange={(e) => { const id = e.target.value; const who = members.data?.members.find((m) => m.user_id === id)?.display_name ?? 'Owner'; if (id) void c.act(() => clientFirmApi.setOwner(c.slug, c.eng, r.request_id, id), `${who} owns ${r.ref}`); }}>
      <option value="">No owner yet</option>
      {(members.data?.members ?? []).filter((m) => m.role !== 'viewer').map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}
    </Select>
  );
}

/** T6-H7: attach several items (and upload a new file) in one version. */
function Attach({ r, c, hasVersion }: { r: PbcRequest; c: Ctx; hasVersion: boolean }) {
  const evidence = useQuery({ queryKey: ['evidence', c.slug], queryFn: () => req<{ artifacts: Array<{ artifact_id: string; title: string }> }>(`/api/w/${c.slug}/evidence`) });
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setUploading(true);
    setUpErr(null);
    try {
      const form = new FormData();
      form.set('title', f.name);
      form.set('type', 'process');
      form.set('file', f);
      const res = await fetch(`/api/w/${encodeURIComponent(c.slug)}/evidence/upload`, { method: 'POST', body: form, credentials: 'include' });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { detail?: string }).detail ?? 'Upload failed');
      const body = (await res.json()) as { artifact: { artifact_id: string } };
      await qc.invalidateQueries({ queryKey: ['evidence', c.slug] });
      setPicked((p) => [...p, body.artifact.artifact_id]);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setUpErr(errText(e));
    } finally {
      setUploading(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (picked.length === 0) return;
    setConfirm(true);
  };
  const list = evidence.data?.artifacts ?? [];
  return (
    <form className="gp-firm-form" onSubmit={submit} noValidate aria-label={`Send evidence for ${r.ref}`}>
      <fieldset className="gp-firm-fieldset">
        <legend>Evidence to send</legend>
        {list.length === 0 ? <p className="gp-firm-muted">No evidence yet — upload a file below.</p> : list.map((a) => (
          <label key={a.artifact_id} className="gp-firm-actions">
            <input type="checkbox" checked={picked.includes(a.artifact_id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, a.artifact_id] : p.filter((x) => x !== a.artifact_id)))} />
            {a.title}
          </label>
        ))}
        {tried && picked.length === 0 ? <p className="gp-firm-err" role="alert">Choose at least one item.</p> : null}
        <div className="gp-firm-form--row">
          <Field id={`up-${r.request_id}`} label="Upload a new file" hint="PDF, DOCX, Markdown or text">
            <input type="file" ref={fileRef} accept=".pdf,.docx,.md,.txt" onChange={() => void upload()} />
          </Field>
          {uploading ? <span className="gp-firm-muted">Uploading…</span> : null}
        </div>
        {upErr ? <Alert variant="error">{upErr}</Alert> : null}
      </fieldset>
      <Field id={`note-${r.request_id}`} label="Note for the auditor (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <div className="gp-firm-actions"><Button type="submit" variant="primary" aria-disabled={c.busy || undefined}>{hasVersion ? 'Send a new version…' : 'Send…'}</Button></div>
      <ActionDialog open={confirm} title={`Send ${plural(picked.length, 'item')} to ${c.t.engagement.firm_name}?`} submitLabel="Send" busy={c.busy} onClose={() => setConfirm(false)}
        onSubmit={async () => {
          if (await c.act(() => clientFirmApi.submitPbc(c.slug, c.eng, r.request_id, picked.map((id) => ({ kind: 'evidence_artifact', id })), note.trim() || null), `${r.ref} sent to ${c.t.engagement.firm_name}`)) {
            setConfirm(false); setPicked([]); setNote(''); setTried(false);
          }
        }}>
        <p>Each send is a new version the auditor keeps; earlier versions stay on file.</p>
      </ActionDialog>
    </form>
  );
}

/** T6-B5: where a population came from — owners, admins and editors record it. */
function PopulationRow({ p, c }: { p: ClientPopulation; c: Ctx }) {
  const [source, setSource] = useState('');
  const [method, setMethod] = useState('system_report');
  const [query, setQuery] = useState('');
  const [when, setWhen] = useState('');
  const [adjusted, setAdjusted] = useState(false);
  const [adjNote, setAdjNote] = useState('');
  const [tried, setTried] = useState(false);
  const errs = { source: source.trim().length < 2 ? 'Name the system it came from.' : null, when: !/^\d{4}-\d{2}-\d{2}$/.test(when) ? 'When was it extracted?' : null, adj: adjusted && adjNote.trim().length < 5 ? 'Say what was changed.' : null };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.values(errs).some(Boolean)) return;
    await c.act(() => clientFirmApi.provenance(c.slug, c.eng, p.population_id, { source_system: source.trim(), extraction_method: method, query_or_report: query.trim() || null, extracted_at: when, adjusted, adjustment_note: adjusted ? adjNote.trim() : null }), 'Source recorded');
  };
  const kind = p.kind.replace(/_/g, ' ').replace(/^\w/, (x) => x.toUpperCase());
  return (
    <li className="gp-firm-row">
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{kind} · {plural(p.item_count, 'item')}</h3>
        {p.provenance ? <Chip>{p.provenance.independence_label}</Chip> : <Chip tone="accent">Record the source</Chip>}
      </div>
      {p.provenance ? (
        <dl className="gp-firm-dl">
          <dt>Source</dt><dd>{p.provenance.source_system} · {p.provenance.extraction_method.replace(/_/g, ' ')}</dd>
          <dt>Extracted</dt><dd>{fmtDate(p.provenance.extracted_at)}</dd>
          {p.provenance.adjusted ? <><dt>Adjusted</dt><dd>{p.provenance.adjustment_note}</dd></> : null}
        </dl>
      ) : c.editor && c.open ? (
        <form className="gp-firm-form" onSubmit={submit} noValidate aria-label={`Record the source of ${kind.toLowerCase()}`}>
          <div className="gp-firm-form--row">
            <Field id={`src-${p.population_id}`} label="Source system" error={tried ? errs.source : null}><Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Workday" /></Field>
            <Field id={`how-${p.population_id}`} label="How it was pulled">
              <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="system_report">System report</option>
                <option value="system_query">System query</option>
                <option value="connector">Connector</option>
                <option value="client_prepared">Prepared by hand</option>
              </Select>
            </Field>
            <Field id={`when-${p.population_id}`} label="Extracted on" error={tried ? errs.when : null}><Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
          </div>
          <Field id={`q-${p.population_id}`} label="Report or query (optional)"><Input value={query} onChange={(e) => setQuery(e.target.value)} /></Field>
          <label className="gp-firm-actions"><input type="checkbox" checked={adjusted} onChange={(e) => setAdjusted(e.target.checked)} /> The list was adjusted after extraction</label>
          {adjusted ? <Field id={`adj-${p.population_id}`} label="What was changed" error={tried ? errs.adj : null}><Input value={adjNote} onChange={(e) => setAdjNote(e.target.value)} /></Field> : null}
          <div className="gp-firm-actions"><Button type="submit" variant="primary" aria-disabled={c.busy || undefined}>Record source</Button></div>
        </form>
      ) : <p className="gp-firm-muted">Source not recorded yet · an owner, admin or editor records it.</p>}
      {p.requested_selections.map((s) => <p key={s.selection_id} className="gp-firm-muted">Selected for testing {fmtDate(s.requested_at)}: {s.item_keys.join(', ')}</p>)}
    </li>
  );
}

function ExceptionRow({ x, c }: { x: ClientException; c: Ctx }) {
  const [resp, setResp] = useState('');
  const [tried, setTried] = useState(false);
  const respId = `resp-${x.exception_id}`;
  const members = useQuery({ queryKey: ['ws-members', c.slug], queryFn: () => listWorkspaceMembers(c.slug), enabled: !c.admin && !c.preview });
  const responders = useMemo(() => (members.data?.members ?? []).filter((m) => m.role === 'owner' || m.role === 'admin').map((m) => m.display_name).join(', '), [members.data]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (resp.trim().length < 10) return;
    if (await c.act(() => clientFirmApi.respond(c.slug, c.eng, x.exception_id, resp.trim(), x.version), `Response to ${x.control_ref} v${x.version} saved`)) { setResp(''); setTried(false); }
  };
  const needs = !x.response || x.needs_response_update;
  return (
    <li className="gp-firm-row" id={`cx-${x.exception_id}`}>
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{x.control_ref} · v{x.version}</h3>
        {needs ? <Chip tone="accent">{x.response ? `Respond to v${x.version}` : 'Needs a response'}</Chip> : <Chip tone="success">Responded</Chip>}
      </div>
      <p>{x.wording}</p>
      {x.response ? <p className="gp-firm-muted">Your response (answers v{x.response.exception_version} · {x.response.unaudited_label.toLowerCase()}): {x.response.body}</p> : null}
      {needs && c.open && !c.preview ? (
        c.admin ? (
          <form className="gp-firm-form" onSubmit={submit} noValidate>
            {x.needs_response_update ? <p className="gp-firm-muted"><a href={`#${respId}`} onClick={(e) => { e.preventDefault(); document.getElementById(respId)?.focus(); }}>Respond to v{x.version}</a> · the exception wording changed since your response</p> : null}
            <Field id={respId} label={`Management response to v${x.version}`} hint="Unaudited · not covered by the opinion" error={tried && resp.trim().length < 10 ? 'Write the response (at least 10 characters).' : null}>
              <Textarea rows={2} value={resp} onChange={(e) => setResp(e.target.value)} />
            </Field>
            <div className="gp-firm-actions"><Button type="submit" variant="primary" aria-disabled={c.busy || undefined}>Save response</Button></div>
          </form>
        ) : <p className="gp-firm-muted">An owner or admin responds{responders ? `: ${responders}` : ''}.</p>
      ) : null}
    </li>
  );
}

/** T6-H5 / T6-B11: draft from the template, edit, submit to the firm with a confirm; re-date a stale letter. */
function DocumentRow({ kind, doc, c }: { kind: string; doc: ReportDocument | null; c: Ctx }) {
  const label = DOC_LABEL[kind] ?? kind;
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const firm = c.t.engagement.firm_name;
  const canWrite = c.admin && c.open && !c.preview;
  const startDraft = async () => {
    setLoading(true);
    setErr(null);
    try {
      const tpl = await clientFirmApi.template(c.slug, c.eng, kind);
      setBody(doc && doc.status === 'returned' && doc.body ? doc.body : tpl.body);
      setEditing(true);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setLoading(false);
    }
  };
  const state = !doc ? 'Not drafted' : doc.stale ? 'Re-date to the report date' : doc.status === 'submitted' ? `Sent · with ${firm}` : doc.status === 'returned' ? 'Returned · revise and resend' : doc.status === 'accepted' ? 'Accepted' : 'Draft';
  return (
    <li className="gp-firm-row">
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{label}{doc ? ` · v${doc.version}` : ''}</h3>
        <Chip tone={!doc || doc.stale || doc.status === 'returned' ? 'accent' : doc.status === 'accepted' ? 'success' : 'neutral'}>{state}</Chip>
      </div>
      {doc?.document_date ? <p className="gp-firm-muted">Dated {fmtDate(doc.document_date)}</p> : null}
      {err ? <Alert variant="error">{err}</Alert> : null}
      {doc?.status === 'returned' && doc.return_comment ? <p className="gp-firm-muted"><strong>{firm}:</strong> {doc.return_comment}</p> : null}
      {canWrite && !editing ? (
        <div className="gp-firm-actions">
          {doc?.stale ? (
            <Button compact variant="primary" aria-disabled={c.busy || undefined} onClick={() => void c.act(() => clientFirmApi.saveDocument(c.slug, c.eng, kind, true), `${label} re-dated and sent to ${firm}`)}>Re-date and resend</Button>
          ) : !doc || doc.status === 'returned' || doc.status === 'draft' ? (
            <Button compact aria-disabled={loading || undefined} onClick={() => void startDraft()}>{doc ? 'Revise…' : 'Draft from template…'}</Button>
          ) : null}
        </div>
      ) : null}
      {canWrite && editing ? (
        <form className="gp-firm-form gp-firm-form--wide" onSubmit={(e) => { e.preventDefault(); if (body.trim().length >= 20) setConfirm(true); }} noValidate>
          <Field id={`doc-${kind}`} label={`${label} draft`} hint="From the AICPA-structured template · edit before you submit" error={body.trim().length < 20 ? 'The document needs at least 20 characters.' : null}>
            <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="gp-firm-actions">
            <Button type="submit" variant="primary">Submit to {firm}…</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
          <ActionDialog open={confirm} title={`Submit the ${label.toLowerCase()} to ${firm}?`} submitLabel="Submit" busy={c.busy} onClose={() => setConfirm(false)}
            onSubmit={async () => { if (await c.act(() => clientFirmApi.saveDocument(c.slug, c.eng, kind, true, body.trim()), `${label} sent to ${firm}`)) { setConfirm(false); setEditing(false); } }}>
            <p>{kind === 'representation_letter' ? `It’s dated to the report date${c.t.engagement.report_date ? ` (${fmtDate(c.t.engagement.report_date)})` : ''}. ` : ''}Each submission is a new version; the firm accepts it or returns it with a comment.</p>
          </ActionDialog>
        </form>
      ) : null}
    </li>
  );
}

/** T6-H9: the owner ends client access; the firm keeps its own papers in custody (ADR-0047 R5). */
function RevokeDialog({ c, onClose }: { c: Ctx; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  return (
    <ActionDialog open title={`Revoke ${c.t.engagement.firm_name}’s access?`} submitLabel="Revoke access" danger busy={c.busy} onClose={onClose}
      onSubmit={async () => {
        setTried(true);
        if (reason.trim().length < 5) return false;
        if (await c.act(() => clientFirmApi.revoke(c.slug, c.eng, reason.trim()), `${c.t.engagement.firm_name}’s access revoked`)) onClose();
        return undefined;
      }}>
      <p>The firm stops seeing your requests, populations and evidence. It keeps its own working papers — read-only, partner and manager only — for its retention period, as audit firms must.</p>
      <Field id="revoke-why" label="Reason" error={tried && reason.trim().length < 5 ? 'Give a reason (at least 5 characters).' : null}>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </ActionDialog>
  );
}
